import React, { useState, useEffect } from "react";
import { useNavigate, Outlet, useParams } from "react-router-dom";
import JobsAppliedContent from "./AppliedJobsComponent";
import WorkerMessagesContent from "../messages_area/WorkerMessagesComponent";
import WorkerAgreementsContent from "../agreements_Area/AdminWorkerAgreementsComponent";
import WorkerClaimsInvoicesBillingComponent from "../worker-user-billing/WorkerClaimsInvoicesBillingComponent";
import ActivityLogsComponent from "../../../activity_logs/ActivityLogsComponent";
import config from "../../../config/config";
import axios from "axios";
import {
  addInlineStyles,
  getImageDetailsForBadge,
  formatBadgeType,
  formatBadgeValue,
  formatText,
} from "../../../util_functions/helpers";
import {
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaRegIdCard,
  FaBriefcase,
  FaMoneyBillAlt,
  FaGraduationCap,
  FaHeart,
  FaShieldAlt,
  FaSyringe,
  FaStar,
  FaTrashAlt,
  FaBan,
  FaUnlockAlt,
  FaDollarSign,
} from "react-icons/fa";
import { TbHistory } from "react-icons/tb";
import { TiMessages } from "react-icons/ti";
import { MdLockReset } from "react-icons/md";
import { CiEdit } from "react-icons/ci";
import { IoMdArrowRoundBack } from "react-icons/io";
import { LiaFileContractSolid } from "react-icons/lia";
import { IoPersonCircle } from "react-icons/io5";
import { LiaTimesCircle } from "react-icons/lia";

const UserDetails = ({
  user,
  onEdit,
  onDeactivate,
  onActivate,
  onDelete,
  toggleLeft,
}) => {
  const baseURL = config.API_BASE_URL;
  const navigate = useNavigate();
  const [activeContent, setActiveContent] = useState(null);
  const [fade, setFade] = useState("fade-in");

  useEffect(() => {
    if (activeContent) {
      setFade("fade-out");
      const timeoutId = setTimeout(() => setFade("fade-in"), 300);
      return () => clearTimeout(timeoutId);
    }
  }, [activeContent]);
  useEffect(() => {
    setActiveContent(null);
  }, [user]);

  if (!user) {
    return (
      <div className="welcome-container">
        <div className="welcome-content">
          <h1>Welcome to the Worker User Management Portal</h1>
          <div className="guidance-text">
            Use this portal to search for worker users and select them to view
            more details. Navigate through the options to manage and update
            worker user information efficiently.
          </div>
        </div>
      </div>
    );
  }

  const profileImageUrl = user?.profilePhoto
    ? `${baseURL}/v1/worker-users/profile-images/${user.profilePhoto}`
    : "/images/avatar.jpg";

  const handleResetPassword = async (user) => {
    try {
      const response = await axios.post(
        `${baseURL}/v1/worker-users/reset-password/${user.id}`
      );
      if (response.data) {
        console.log("Password reset successfully for user:", user.email);
        alert(
          "Password reset successfully. The new password has been sent to the user's email."
        );
      } else {
        console.error("Unexpected response status:", response.status);
        alert("Failed to reset password. Please try again later.");
      }
    } catch (error) {
      console.error("Error resetting password:", error);
      alert("Failed to reset password. Please try again later.");
    }
  };

  const handleNavigation = (path) => {
    setActiveContent(path);
  };

  const renderContent = () => {
    switch (activeContent) {
      case "applied-jobs":
        return <JobsAppliedContent userId={user.id} />;
      case "messages":
        return <WorkerMessagesContent userId={user.id} />;
      case "agreements":
        return <WorkerAgreementsContent user={user} toggleLeft={toggleLeft} />;
      case "billing":
        return (
          <WorkerClaimsInvoicesBillingComponent
            user={user}
            toggleLeft={toggleLeft}
          />
        );
      case "invoices":
        return <div>Invoices Content</div>;
      case "activityLog":
        return <ActivityLogsComponent user={user} />;
      default:
        return (
          <div className="user-details-content">
            <div className="user-details-header">
              <div className="user-details-header-inner">
                <div className="user-details-profile-photo">
                  <img
                    src={profileImageUrl}
                    alt={`${user.firstName} ${user.lastName}`}
                  />
                </div>
                <div className="user-basic-info">
                  <h2>
                    {user.firstName} {user.lastName}
                  </h2>
                  <p>
                    <FaEnvelope className="user-details-icon-email" />
                    {user.email}
                  </p>
                  <p>
                    <FaPhone className="user-details-icon-phone" />
                    {user.mobileNumber}
                  </p>
                  <p>
                    <FaMapMarkerAlt className="user-details-icon-place" />
                    {user.primaryLocation}
                  </p>
                </div>
              </div>
              <div>
                <div className="user-actions">
                  <button
                    className="default"
                    onClick={() => handleNavigation(`applied-jobs`)}
                  >
                    <FaBriefcase /> Jobs Applied
                  </button>
                  <button
                    className="default"
                    onClick={() => setActiveContent("messages")}
                  >
                    <TiMessages /> Messages
                  </button>
                  <button
                    className="default"
                    onClick={() => setActiveContent("agreements")}
                  >
                    <LiaFileContractSolid /> Agreements
                  </button>
                  <button
                    className="default-billing"
                    onClick={() => setActiveContent("billing")}
                  >
                    <FaDollarSign /> Claims - Invoices - Payments
                  </button>
                </div>
                <div className="user-actions">
                  <button className="edit" onClick={() => onEdit(user)}>
                    <CiEdit /> Edit User
                  </button>
                  <button
                    className="reset"
                    onClick={() => handleResetPassword(user)}
                  >
                    <MdLockReset /> Reset Password
                  </button>
                  {user.userLocked ? (
                    <button
                      className="deactivate"
                      onClick={() => onDeactivate(user)}
                    >
                      <FaBan /> Unlock User
                    </button>
                  ) : (
                    <button
                      className="activate"
                      onClick={() => onActivate(user)}
                    >
                      <FaUnlockAlt /> Lock User
                    </button>
                  )}
                  <button className="delete" onClick={() => onDelete(user)}>
                    <FaTrashAlt /> Delete
                  </button>
                  <button
                    className="default"
                    onClick={() => setActiveContent("activityLog")}
                  >
                    <TbHistory /> Activity Log
                  </button>
                </div>
              </div>
            </div>
            <div className="user-details-section">
              <h3>
                <IoPersonCircle /> About me (Bio)
              </h3>
              <div
                className="user-details-bio"
                dangerouslySetInnerHTML={{
                  __html: addInlineStyles(user.bioData.bioText),
                }}
              ></div>
            </div>
            <div className="user-details-section">
              <h3>
                <FaRegIdCard /> Personal Information
              </h3>
              <ul>
                <li>
                  <strong>Gender &nbsp; </strong>&nbsp;
                  <span className="user-detail-arrow">➤</span>
                  <div className="userdetails-section-item">
                    {user.gender || "N/A"}
                  </div>
                </li>
                <li>
                  <strong>Religion &nbsp; </strong>&nbsp;
                  <span className="user-detail-arrow">➤</span>
                  <div className="userdetails-section-item">
                    {user.religion && user.religion.length > 0
                      ? user.religion.join(", ")
                      : "N/A"}
                  </div>
                </li>
                <li>
                  <strong>Cultural Background &nbsp; </strong>&nbsp;
                  <span className="user-detail-arrow">➤</span>
                  <div className="userdetails-section-item">
                    {user.culturalBackground &&
                    user.culturalBackground.length > 0
                      ? user.culturalBackground.join(", ")
                      : "N/A"}
                  </div>
                </li>
                <li>
                  <strong>Main Languages &nbsp; </strong>&nbsp;
                  <span className="user-detail-arrow">➤</span>
                  <div className="userdetails-section-item">
                    {user.languages &&
                    user.languages.mainLanguages &&
                    user.languages.mainLanguages.length > 0
                      ? user.languages.mainLanguages.join(", ")
                      : "N/A"}
                  </div>
                </li>
                <li>
                  <strong>Other Languages &nbsp; </strong>&nbsp;
                  <span className="user-detail-arrow">➤</span>
                  <div className="userdetails-section-item">
                    {user.languages &&
                    user.languages.otherLanguages &&
                    user.languages.otherLanguages.length > 0
                      ? user.languages.otherLanguages.join(", ")
                      : "N/A"}
                  </div>
                </li>
              </ul>
            </div>
            <div className="user-details-section">
              <h3>
                <FaBriefcase /> Availability
              </h3>
              <ul>
                <li>
                  <strong>
                    Total Support Hours Per Week &nbsp;
                    <span className="user-detail-arrow">➤</span>
                  </strong>
                  <div className="userdetails-section-item">
                    <strong>
                      {user.availability.totalSupportHoursPerWeek}
                    </strong>
                  </div>
                </li>
              </ul>
              <div className="user-details-experience-discipline">
                <p>
                  <strong>Daily Availability</strong>
                </p>
              </div>
              <ul>
                {Object.entries(user.availability.dailyAvailability).map(
                  ([day, times]) => (
                    <li
                      key={day}
                      className={
                        times.length > 0 ? "available" : "not-available"
                      }
                    >
                      <strong>
                        {day}
                        {times.length === 0 && (
                          <LiaTimesCircle className="worker-availability-icon wa-red-icon" />
                        )}
                      </strong>
                      &nbsp;
                      {times.length > 0 && (
                        <>
                          <span className="user-detail-arrow">➤</span>
                          &nbsp;
                          <div className="userdetails-section-item">
                            {times
                              .map((time) => `${time.from} - ${time.to}`)
                              .join(", ")}
                          </div>
                        </>
                      )}
                    </li>
                  )
                )}
              </ul>
            </div>
            <div className="user-details-section">
              <h3>
                <FaBriefcase /> Experience
              </h3>
              {user.experience.experiences.map((experience, index) => (
                <div key={index}>
                  <div className="user-details-experience-discipline">
                    <p>
                      <strong>
                        Discipline &nbsp;
                        <span className="user-detail-arrow">➤</span>&nbsp;
                      </strong>
                      {experience.discipline}
                    </p>
                  </div>
                  <ul>
                    {experience.skills.map((skill, idx) => {
                      let label;
                      if (skill.professional && skill.personal) {
                        label = (
                          <span className="exp-tag exp-both">
                            Professional and Personal Experience
                          </span>
                        );
                      } else if (skill.professional) {
                        label = (
                          <span className="exp-tag exp-professional">
                            Professional Experience
                          </span>
                        );
                      } else if (skill.personal) {
                        label = (
                          <span className="exp-tag exp-personal">
                            Personal Experience
                          </span>
                        );
                      }

                      if (label) {
                        return (
                          <li key={idx}>
                            {skill.name} &nbsp;&nbsp;
                            <span className="user-detail-arrow">➤</span>
                            {label}
                          </li>
                        );
                      }
                      return null;
                    })}
                  </ul>
                </div>
              ))}
            </div>
            <div className="user-details-section">
              <h3>
                <FaMoneyBillAlt /> Service Rates
              </h3>
              <ul>
                {Object.entries(user.serviceRates).map(
                  ([rateType, rateValue]) => {
                    const hourlyRateTypes = [
                      "Night Shift",
                      "Weekday",
                      "Saturday",
                      "Sunday",
                    ];
                    const isHourlyRate = hourlyRateTypes.includes(rateType);

                    const displayRate =
                      rateValue === "0" ? "Free" : `$${rateValue}`;

                    return (
                      <li key={rateType}>
                        {rateType}&nbsp;
                        <span className="user-detail-arrow">➤</span>
                        <div className="userdetails-section-item">
                          <strong>{displayRate}</strong>
                          {isHourlyRate && " / hr"}
                        </div>
                      </li>
                    );
                  }
                )}
              </ul>
            </div>

            <div className="user-details-section">
              <h3>
                <FaGraduationCap /> Education / Training History
              </h3>
              {user.educationHistory.map((education, index) => (
                <div key={index} className="user-details-education-section">
                  <ul>
                    <li>
                      <strong>Institution &nbsp;</strong>&nbsp;
                      <span className="user-detail-arrow">➤</span>
                      <div className="userdetails-section-item">
                        {education.institution}
                      </div>
                    </li>
                    <li>
                      <strong>Course/Degree/Certificate &nbsp;</strong>&nbsp;
                      <span className="user-detail-arrow">➤</span>
                      <div className="userdetails-section-item">
                        {education.courseDegree}
                      </div>
                    </li>
                    <li>
                      <strong>Start Date &nbsp;</strong>&nbsp;
                      <span className="user-detail-arrow">➤</span>
                      <div className="userdetails-section-item">
                        {education.startMonth}, {education.startYear}
                      </div>
                    </li>
                    <li>
                      <strong>End Date &nbsp;</strong>&nbsp;
                      <span className="user-detail-arrow">➤</span>
                      <div className="userdetails-section-item">
                        {education.endMonth}, {education.endYear}
                      </div>
                    </li>
                  </ul>
                </div>
              ))}
            </div>
            <div className="user-details-section">
              <h3>
                <FaBriefcase /> Employment History
              </h3>
              {user.employmentHistory.map((employment, index) => (
                <div key={index} className="user-details-education-section">
                  <ul>
                    <li>
                      <strong>Title &nbsp;</strong>&nbsp;
                      <span className="user-detail-arrow">➤</span>
                      <div className="userdetails-section-item">
                        {employment.title}
                      </div>
                    </li>
                    <li>
                      <strong>Company &nbsp;</strong>&nbsp;
                      <span className="user-detail-arrow">➤</span>
                      <div className="userdetails-section-item">
                        {employment.company}
                      </div>
                    </li>
                    <li>
                      <strong>Start Date &nbsp;</strong>&nbsp;
                      <span className="user-detail-arrow">➤</span>
                      <div className="userdetails-section-item">
                        {employment.startMonth}, {employment.startYear}
                      </div>
                    </li>
                    <li>
                      <strong>End Date &nbsp;</strong>&nbsp;
                      <span className="user-detail-arrow">➤</span>
                      <div className="userdetails-section-item">
                        {employment.endMonth}, {employment.endYear}
                      </div>
                    </li>
                    <li>
                      <strong>Current Role &nbsp;</strong>&nbsp;
                      <span className="user-detail-arrow">➤</span>
                      <div className="userdetails-section-item">
                        {employment.currentRole ? "Yes" : "No"}
                      </div>
                    </li>
                  </ul>
                </div>
              ))}
            </div>
            <div className="user-details-section">
              <h3>
                <FaHeart /> Interests and Hobbies
              </h3>
              <ul>
                {user.interestsAndHobbies.map((hobby, index) => (
                  <li key={index}>{hobby}</li>
                ))}
              </ul>
            </div>
            <div className="user-details-section-badge">
              <h3>
                <FaShieldAlt /> Badges
              </h3>
              <ul>
                {Object.entries(user.badge).map(([badgeType, badgeValue]) => {
                  const { src, alt } = getImageDetailsForBadge(badgeType);
                  return (
                    <li key={badgeType}>
                      <img
                        src={src}
                        alt={alt}
                        style={{
                          width: "30px",
                          height: "28px",
                          marginRight: "10px",
                        }}
                      />
                      <strong>{formatBadgeType(badgeType)}</strong>
                      {badgeValue.toLowerCase() === "yes" && (
                        <>
                          &nbsp;
                          <span className="user-detail-arrow">&nbsp;➤</span>
                          <span>&nbsp;{formatBadgeValue(badgeValue)}</span>
                        </>
                      )}
                      {badgeValue.toLowerCase() === "no" && (
                        <LiaTimesCircle className="worker-availability-icon wa-red-icon" />
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
            <div className="user-details-section">
              <h3>
                <FaRegIdCard /> Languages
              </h3>
              <ul>
                <li>
                  <strong>Main Languages &nbsp; </strong>&nbsp;
                  <span className="user-detail-arrow">➤</span>
                  <div className="userdetails-section-item">
                    {user.languages.mainLanguages &&
                    user.languages.mainLanguages.length > 0
                      ? user.languages.mainLanguages.join(", ")
                      : "N/A"}
                  </div>
                </li>
                <li>
                  <strong>Other Languages &nbsp; </strong>&nbsp;
                  <span className="user-detail-arrow">➤</span>
                  <div className="userdetails-section-item">
                    {user.languages.otherLanguages &&
                    user.languages.otherLanguages.length > 0
                      ? user.languages.otherLanguages.join(", ")
                      : "N/A"}
                  </div>
                </li>
              </ul>
            </div>
            <div className="user-details-section">
              <h3>
                <FaSyringe /> Immunization Status
              </h3>
              <ul>
                <li>
                  <strong>COVID Vaccine Status &nbsp;</strong>&nbsp;
                  <span className="user-detail-arrow">➤</span>
                  <div className="userdetails-section-item">
                    {formatText(user.immunizationStatus.covidVaccineStatus)}
                  </div>
                </li>
                <li>
                  <strong>Flu Vaccine Status &nbsp;</strong>&nbsp;
                  <span className="user-detail-arrow">➤</span>
                  <div className="userdetails-section-item">
                    {formatText(user.immunizationStatus.fluVaccineStatus)}
                  </div>
                </li>
              </ul>
            </div>

            <div className="user-details-section">
              <h3>
                <FaStar /> Reviews
              </h3>
              {user.reviews.map((review, index) => (
                <div key={index}>
                  <p>
                    <strong>Comment:</strong> {review.comment}
                  </p>
                  <p>
                    <strong>Rating:</strong> {review.rating}
                  </p>
                  <p>
                    <strong>Date:</strong> {review.dateCreated}
                  </p>
                </div>
              ))}
            </div>
          </div>
        );
    }
  };

  return (
    <div>
      <div className="user-details-close-button-container">
        {activeContent && (
          <button
            className="user-details-close-button"
            onClick={() => {
              setActiveContent(null);
              navigate(
                `/account/admin-dashboard/users/worker-users/${user.id}`
              );
              if (toggleLeft) {
                toggleLeft(false);
              }
            }}
          >
            <IoMdArrowRoundBack className="user-details-back-icon" /> User
            Details
          </button>
        )}
      </div>

      <div className="user-details-content-container">{renderContent()}</div>
    </div>
  );
};

export default UserDetails;
